import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from './AuthContext';
import './Login.css';

function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Validate inputs
    if (!email || !password) {
      setError('Please fill in all fields! 📝');
      setIsLoading(false);
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long! 🔒');
      setIsLoading(false);
      return;
    }

    try {
      // Call the login function from AuthContext
      await login(email, password);
      // If successful, navigate to home
      navigate('/');
    } catch (err) {
      setError(err.message || 'Login failed. Please try again! ❌');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-shell">
      {/* Decorative background */}
      <div className="login-bg-decoration"></div>

      <div className="login-container">
        {/* Logo and title */}
        <div className="login-header">
          <div className="login-logo">🏥</div>
          <h1 className="login-title">Feelio</h1>
          <p className="login-subtitle">
            Child Emotion Learning Platform<br />
            <span className="login-subtitle-small">For Authorized Medical Practitioners</span>
          </p>
        </div>

        {/* Login card */}
        <div className="login-card">
          <h2 className="login-form-title">Practitioner Login</h2>

          {/* Error message */}
          {error && (
            <div className="login-error">
              <span className="error-icon">⚠️</span>
              <span>{error}</span>
            </div>
          )}

          {/* Login form */}
          <form onSubmit={handleSubmit} className="login-form">
            {/* Email field */}
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                � Username / License ID
              </label>
              <input
                id="email"
                type="text"
                placeholder="Enter your username (e.g., dr.smith)"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="form-input"
                disabled={isLoading}
              />
            </div>

            {/* Password field */}
            <div className="form-group">
              <label htmlFor="password" className="form-label">
                🔒 Password
              </label>
              <div className="password-wrapper">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="form-input"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>

            {/* Login button */}
            <button
              type="submit"
              className="login-btn"
              disabled={isLoading}
            >
              {isLoading ? '🔄 Logging in...' : '✨ Login'}
            </button>

            {/* Register link */}
            <p style={{
              textAlign: 'center',
              fontSize: '14px',
              color: '#4a5568',
              margin: '4px 0 0 0',
              fontWeight: '600',
            }}>
              New practitioner?{' '}
              <Link
                to="/register"
                style={{ color: '#1e40af', textDecoration: 'none', fontWeight: '800' }}
              >
                Create an account
              </Link>
            </p>
            <p style={{ textAlign: 'center', fontSize: '13px', color: '#718096', margin: '4px 0 0', fontWeight: '600' }}>
              Administrator?{' '}
              <Link to="/admin/login" style={{ color: '#4338ca', textDecoration: 'none', fontWeight: '800' }}>
                Admin Login →
              </Link>
            </p>

            {/* Helper info */}
            <div className="login-info-box">
              <p className="info-text">
                🔐 <strong>Authorized Practitioners Only</strong><br />
                Contact your clinic administrator for access credentials.
              </p>
            </div>
          </form>
        </div>

        {/* Footer info */}
        <div className="login-footer">
          <p>
            � <strong>Secure & HIPAA Compliant:</strong> This system is designed for authorized medical practitioners only. All patient data is encrypted and protected.
          </p>
          <p className="login-footer-secondary">
            For access requests or technical support, contact your clinic administrator.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
